import React, { useState, useEffect, useCallback, useRef } from 'react';
import { saldoAPI, transactionAPI } from '../services/api';
import { formatRupiah, formatDateTime } from '../utils/helpers';
import { PageHeader, Modal, Loader, EmptyState } from '../components/UI';
import {
  Wallet, ArrowUpCircle, ArrowDownCircle, ArrowLeftRight,
  RefreshCw, Edit3, TrendingUp, TrendingDown, AlertTriangle,
  History, Plus, Trash2, Settings, Loader2, GripVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const GROUP_ORDER = ['Server Pulsa', 'Bank', 'E-Wallet', 'Tunai'];

const DIGITAL_MENU_OPTIONS = [
  { key: 'pulsa',         label: 'Pulsa' },
  { key: 'kuota',         label: 'Kuota' },
  { key: 'topup_ewallet', label: 'Top Up E-Wallet' },
  { key: 'topup_game',    label: 'Top Up Game' },
  { key: 'token_listrik', label: 'Token Listrik' },
  { key: 'transfer',      label: 'Transfer' },
  { key: 'tarik_tunai',   label: 'Tarik Tunai' },
  { key: 'tagihan',       label: 'Tagihan' },
];

const DEFAULT_MENU_ORDER = DIGITAL_MENU_OPTIONS.map(o => o.key);

const ICON_OPTIONS = [
  { file: 'bank.png',        label: 'Bank' },
  { file: 'bca.png',         label: 'BCA' },
  { file: 'bni.png',         label: 'BNI' },
  { file: 'bri.png',         label: 'BRI' },
  { file: 'brilink.png',     label: 'BRILink' },
  { file: 'dana.png',        label: 'DANA' },
  { file: 'digipos.png',     label: 'Digipos' },
  { file: 'gopay.png',       label: 'GoPay' },
  { file: 'isaku.png',       label: 'iSaku' },
  { file: 'kastunai.png',    label: 'Kas Tunai' },
  { file: 'linkaja.png',     label: 'LinkAja' },
  { file: 'mitrabl.png',     label: 'Mitra BL' },
  { file: 'ovo.png',         label: 'OVO' },
  { file: 'permata.png',     label: 'Permata' },
  { file: 'qris.png',        label: 'QRIS' },
  { file: 'radarpulsa.png',  label: 'Radar Pulsa' },
  { file: 'seabank.png',     label: 'SeaBank' },
  { file: 'shopeepay.png',   label: 'ShopeePay' },
];

const getAkunIcon = (akunId = '') => {
  const id = String(akunId).toLowerCase();
  if (id.includes('brilink'))  return 'brilink.png';
  if (id.includes('brimo'))    return 'bri.png';
  if (id.includes('bri'))      return 'bri.png';
  if (id.includes('bca'))      return 'bca.png';
  if (id.includes('bni'))      return 'bni.png';
  if (id.includes('permata'))  return 'permata.png';
  if (id.includes('seabank'))  return 'seabank.png';
  if (id.includes('dana'))     return 'dana.png';
  if (id.includes('ovo'))      return 'ovo.png';
  if (id.includes('gopay'))    return 'gopay.png';
  if (id.includes('shopee'))   return 'shopeepay.png';
  if (id.includes('linkaja'))  return 'linkaja.png';
  if (id.includes('qris'))     return 'qris.png';
  if (id.includes('digipos'))  return 'digipos.png';
  if (id.includes('isaku'))    return 'isaku.png';
  if (id.includes('radar'))    return 'radarpulsa.png';
  if (id.includes('mitra'))    return 'mitrabl.png';
  if (id.includes('tunai'))    return 'kastunai.png';
  return 'bank.png';
};

const iconSrc = (akun) => `/icons/${akun?.iconFile || getAkunIcon(akun?.akunId)}`;

export default function SaldoPage() {
  const { isAdmin } = useAuth();
  const [saldos, setSaldos] = useState([]);
  const [totalSaldo, setTotalSaldo] = useState(0);
  const [kasTunaiData, setKasTunaiData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mutasi
  const [selectedAkun, setSelectedAkun] = useState(null);
  const [mutasi, setMutasi] = useState([]);
  const [mutasiLoading, setMutasiLoading] = useState(false);
  const [showMutasi, setShowMutasi] = useState(false);

  // Top Up
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpForm, setTopUpForm] = useState({ akunId: '', amount: '', keterangan: '' });

  // Transfer
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({ fromAkunId: '', toAkunId: '', amount: '', biayaTransfer: '', keterangan: '' });

  // Koreksi
  const [showKoreksi, setShowKoreksi] = useState(false);
  const [koreksiForm, setKoreksiForm] = useState({ akunId: '', saldoBaru: '', keterangan: '' });

  // Riwayat Transaksi per Akun
  const [showRiwayatTx, setShowRiwayatTx] = useState(false);
  const [riwayatTx, setRiwayatTx] = useState([]);
  const [riwayatTxLoading, setRiwayatTxLoading] = useState(false);
  const [riwayatTxAkun, setRiwayatTxAkun] = useState(null);
  const [riwayatTxPage, setRiwayatTxPage] = useState(1);
  const [riwayatTxPages, setRiwayatTxPages] = useState(1);

  // Edit Transaksi
  const [showEditTx, setShowEditTx] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editForm, setEditForm] = useState({ sellPrice: '', purchasePrice: '', cashback: '' });
  const [editSaving, setEditSaving] = useState(false);

  // Kelola Akun
  const [showKelolaAkun, setShowKelolaAkun] = useState(false);
  const [semuaAkun, setSemuaAkun] = useState([]);
  const [showTambahAkun, setShowTambahAkun] = useState(false);
  const [showEditAkun, setShowEditAkun] = useState(false);
  const [selectedAkunEdit, setSelectedAkunEdit] = useState(null);
  const [savingAkun, setSavingAkun] = useState(false);
  const [akunForm, setAkunForm] = useState({ akunId: '', namaAkun: '', group: 'Bank', icon: '💳', iconFile: 'bank.png' });
  const [draggedMenuKey, setDraggedMenuKey] = useState(null);

  const [saving, setSaving] = useState(false);

  // Session memory: akunId → iconFile. Preserves user's icon choice even if
  // backend response doesn't return iconFile yet (until backend migration lands).
  const iconMemoryRef = useRef({});

  const applyIconMemory = (arr) => arr.map(s =>
    s.iconFile ? s : (iconMemoryRef.current[s.akunId] ? { ...s, iconFile: iconMemoryRef.current[s.akunId] } : s)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saldoAPI.getAll();
      const all = applyIconMemory(data.data || []);
      const kas = all.find(s => s.akunId === 'tunai' || s.akunId.startsWith('tunai'));
      setKasTunaiData(kas || null);
      setSaldos(all.filter(s => !s.akunId.startsWith('tunai')));
      setTotalSaldo(all.filter(s => !s.akunId.startsWith('tunai')).reduce((s, a) => s + a.saldo, 0));
    } catch { toast.error('Gagal memuat saldo'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMutasi = async (akun) => {
    setSelectedAkun(akun);
    setShowMutasi(true);
    setMutasiLoading(true);
    try {
      const { data } = await saldoAPI.getMutasi(akun.akunId);
      setMutasi(data.data || []);
    } catch { toast.error('Gagal memuat mutasi'); }
    finally { setMutasiLoading(false); }
  };

  const loadRiwayatTx = async (akun, page = 1) => {
    setRiwayatTxAkun(akun);
    setShowRiwayatTx(true);
    setRiwayatTxLoading(true);
    try {
      const { data } = await transactionAPI.getPerSumber(akun.akunId, { page, limit: 15 });
      setRiwayatTx(data.data || []);
      setRiwayatTxPages(data.pages || 1);
      setRiwayatTxPage(page);
    } catch { toast.error('Gagal memuat riwayat'); }
    finally { setRiwayatTxLoading(false); }
  };

  const loadSemuaAkun = async () => {
    try {
      const { data } = await saldoAPI.getAllAdmin();
      setSemuaAkun(applyIconMemory(data.data || []));
    } catch { toast.error('Gagal memuat akun'); }
  };

  const openEditTx = (tx, item) => {
    setSelectedTx(tx);
    setSelectedItem(item);
    setEditForm({
      sellPrice: item.sellPrice || '',
      purchasePrice: item.modalAmount || item.purchasePrice || '',
      cashback: item.cashback || 0,
    });
    setShowEditTx(true);
  };

  const handleEditTx = async () => {
    setEditSaving(true);
    try {
      await transactionAPI.editItem(selectedTx._id, selectedItem._id, editForm);
      toast.success('Transaksi berhasil diupdate! ✅');
      setShowEditTx(false);
      loadRiwayatTx(riwayatTxAkun, riwayatTxPage);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal update'); }
    finally { setEditSaving(false); }
  };

  const handleTopUp = async () => {
    if (!topUpForm.akunId || !topUpForm.amount) return toast.error('Lengkapi data!');
    setSaving(true);
    try {
      await saldoAPI.topUp(topUpForm);
      toast.success('Saldo berhasil ditambahkan! ✅');
      setShowTopUp(false);
      setTopUpForm({ akunId: '', amount: '', keterangan: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal top up'); }
    finally { setSaving(false); }
  };

  const handleTransfer = async () => {
    if (!transferForm.fromAkunId || !transferForm.toAkunId || !transferForm.amount) return toast.error('Lengkapi data!');
    setSaving(true);
    try {
      await saldoAPI.transfer(transferForm);
      toast.success('Transfer berhasil! ✅');
      setShowTransfer(false);
      setTransferForm({ fromAkunId: '', toAkunId: '', amount: '', biayaTransfer: '', keterangan: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal transfer'); }
    finally { setSaving(false); }
  };

  const handleKoreksi = async () => {
    if (!koreksiForm.akunId || koreksiForm.saldoBaru === '') return toast.error('Lengkapi data!');
    setSaving(true);
    try {
      await saldoAPI.koreksi(koreksiForm);
      toast.success('Saldo dikoreksi! ✅');
      setShowKoreksi(false);
      setKoreksiForm({ akunId: '', saldoBaru: '', keterangan: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal koreksi'); }
    finally { setSaving(false); }
  };

  const handleTambahAkun = async () => {
    if (!akunForm.akunId || !akunForm.namaAkun) return toast.error('ID Akun dan Nama wajib diisi!');
    setSavingAkun(true);
    try {
      const payload = { ...akunForm, iconFile: akunForm.iconFile || 'bank.png' };
      await saldoAPI.tambahAkun(payload);
      iconMemoryRef.current[payload.akunId] = payload.iconFile;
      toast.success('Akun berhasil ditambahkan! ✅');
      setShowTambahAkun(false);
      setAkunForm({ akunId: '', namaAkun: '', group: 'Bank', icon: '💳', iconFile: 'bank.png' });
      loadSemuaAkun(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal tambah akun'); }
    finally { setSavingAkun(false); }
  };

  const handleUpdateAkun = async () => {
    setSavingAkun(true);
    try {
      const payload = { ...akunForm, iconFile: akunForm.iconFile || getAkunIcon(selectedAkunEdit.akunId) };
      await saldoAPI.updateAkun(selectedAkunEdit.akunId, payload);
      const akunId = selectedAkunEdit.akunId;
      iconMemoryRef.current[akunId] = payload.iconFile;
      setSaldos(prev => prev.map(s => s.akunId === akunId ? { ...s, ...payload } : s));
      setSemuaAkun(prev => prev.map(a => a.akunId === akunId ? { ...a, ...payload } : a));
      setKasTunaiData(prev => prev && prev.akunId === akunId ? { ...prev, ...payload } : prev);
      toast.success('Akun berhasil diupdate! ✅');
      setShowEditAkun(false);
      loadSemuaAkun(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal update akun'); }
    finally { setSavingAkun(false); }
  };

  const handleDeleteAkun = async (akun) => {
    if (!window.confirm(`Nonaktifkan akun ${akun.namaAkun}?`)) return;
    try {
      await saldoAPI.deleteAkun(akun.akunId);
      toast.success('Akun dinonaktifkan!');
      loadSemuaAkun(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal nonaktifkan'); }
  };

  const openTopUp = (akun) => {
    setTopUpForm({ akunId: akun.akunId, amount: '', keterangan: '' });
    setShowTopUp(true);
  };

  const openKoreksi = (akun) => {
    setKoreksiForm({ akunId: akun.akunId, saldoBaru: akun.saldo, keterangan: '' });
    setShowKoreksi(true);
  };

  const getSaldosByGroup = (group) => saldos.filter(s => s.group === group);

  const allSaldos = [...saldos, ...(kasTunaiData ? [kasTunaiData] : [])];

  const formatDisp = (v) => {
    if (!v && v !== 0) return '';
    const n = v.toString().replace(/\D/g, '');
    return n ? new Intl.NumberFormat('id-ID').format(parseInt(n)) : '';
  };

  const getJenisMutasi = (keterangan = '') => {
    const k = keterangan.toLowerCase();
    if (k.includes('penjualan cash') || k.includes('transaksi tunai')) return { label: 'Penjualan', cls: 'bg-blue-100 text-blue-700' };
    if (k.includes('tarik tunai') || k.includes('kas keluar')) return { label: 'Tarik Tunai', cls: 'bg-orange-100 text-orange-700' };
    if (k.includes('transfer')) return { label: 'Transfer', cls: 'bg-orange-100 text-orange-700' };
    if (k.includes('top up') || k.includes('pemasukan')) return { label: 'Top Up', cls: 'bg-green-100 text-green-700' };
    if (k.includes('pengeluaran')) return { label: 'Pengeluaran', cls: 'bg-red-100 text-red-700' };
    if (k.includes('koreksi')) return { label: 'Koreksi', cls: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Lainnya', cls: 'bg-slate-100 text-slate-600' };
  };

  const SumberDanaSelect = ({ value, onChange, label = 'Akun', excludeAkunId = '' }) => (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">-- Pilih Akun --</option>
        {GROUP_ORDER.map(g => {
          const group = allSaldos.filter(s => s.group === g && s.akunId !== excludeAkunId);
          if (!group.length) return null;
          return (
            <optgroup key={g} label={g}>
              {group.map(s => (
                <option key={s.akunId} value={s.akunId}>
                  {s.icon} {s.namaAkun} — {formatRupiah(s.saldo)}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
      {value && (() => {
        const info = allSaldos.find(s => s.akunId === value);
        return info ? (
          <div className={`mt-1.5 flex justify-between px-3 py-2 rounded-lg text-xs ${info.saldo < 50000 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
            <span>Saldo {info.namaAkun}</span>
            <span className="font-bold">{formatRupiah(info.saldo)}</span>
          </div>
        ) : null;
      })()}
    </div>
  );

  const SaldoKartu = ({ akun }) => (
    <div className={`card hover:shadow-card-hover transition-all ${akun.saldo < 0 ? 'border-red-200 bg-red-50' : akun.saldo < 50000 ? 'border-yellow-200 bg-yellow-50' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <img src={iconSrc(akun)} alt={akun.namaAkun} className="w-10 h-10 object-contain rounded-lg" />
          <div>
            <p className="text-sm font-bold text-slate-700">{akun.namaAkun}</p>
            <p className="text-xs text-slate-400">{akun.group}</p>
          </div>
        </div>
        {akun.saldo < 50000 && akun.saldo >= 0 && <span className="badge badge-yellow text-xs">Rendah</span>}
        {akun.saldo < 0 && <span className="badge badge-red text-xs">Minus!</span>}
      </div>

      <p className={`text-xl font-black mb-3 ${akun.saldo < 0 ? 'text-red-600' : 'text-slate-800'}`}>
        {formatRupiah(akun.saldo)}
      </p>

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => loadMutasi(akun)} className="btn btn-outline py-1.5 px-2.5 text-xs flex-1">
          <ArrowLeftRight size={12} /> Mutasi
        </button>
        <button onClick={() => loadRiwayatTx(akun)} className="btn btn-outline py-1.5 px-2.5 text-xs flex-1">
          <History size={12} /> Transaksi
        </button>
        <button onClick={() => openTopUp(akun)} className="btn btn-success py-1.5 px-2.5 text-xs flex-1">
          <ArrowUpCircle size={12} /> Top Up
        </button>
        {isAdmin && (
          <button onClick={() => openKoreksi(akun)} className="btn btn-outline py-1.5 px-2.5 text-xs">
            <Edit3 size={12} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in-up pb-24 lg:pb-0">
      <PageHeader
        title="Mutasi Saldo"
        subtitle="Kelola saldo semua akun — server pulsa, bank, e-wallet"
        actions={
          <div className="flex gap-2">
            <button className="btn btn-outline" onClick={load}><RefreshCw size={16} /> Refresh</button>
            {isAdmin && (
              <button className="btn btn-outline" onClick={() => { loadSemuaAkun(); setShowKelolaAkun(true); }}>
                <Settings size={16} /> Kelola Akun
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setShowTransfer(true)}>
              <ArrowLeftRight size={16} /> Transfer Saldo
            </button>
          </div>
        }
      />

      {/* Total Saldo Digital */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div className="card bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm font-medium mb-1">Total Saldo Digital</p>
              <p className="text-2xl font-black">{formatRupiah(totalSaldo)}</p>
              <p className="text-blue-200 text-xs mt-1">Server Pulsa, Bank, E-Wallet</p>
            </div>
            <Wallet size={36} className="text-blue-300" />
          </div>
        </div>
        <div className={`card border-0 text-white ${(kasTunaiData?.saldo || 0) < 50000 ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-green-600 to-green-700'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm font-medium mb-1">💵 Kas Tunai (Fisik)</p>
              <p className="text-2xl font-black">{formatRupiah(kasTunaiData?.saldo || 0)}</p>
              <p className="text-white/70 text-xs mt-1">Uang fisik di laci/kas</p>
            </div>
            {(kasTunaiData?.saldo || 0) < 50000
              ? <AlertTriangle size={36} className="text-white/70" />
              : <span className="text-4xl">💵</span>}
          </div>
          {kasTunaiData && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-white/20">
              <button
                onClick={() => loadMutasi(kasTunaiData)}
                className="flex-1 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold flex items-center justify-center gap-1"
              >
                ↕ Mutasi
              </button>
              {isAdmin && (
                <button
                  onClick={() => { setKoreksiForm({ akunId: kasTunaiData.akunId, saldoBaru: String(kasTunaiData.saldo), keterangan: '' }); setShowKoreksi(true); }}
                  className="flex-1 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold flex items-center justify-center gap-1"
                >
                  ✏️ Koreksi
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? <Loader /> : (
        <div className="space-y-6">
          {GROUP_ORDER.filter(g => g !== 'Tunai').map(group => {
            const groupSaldos = getSaldosByGroup(group);
            if (!groupSaldos.length) return null;
            const groupTotal = groupSaldos.reduce((s, a) => s + a.saldo, 0);
            return (
              <div key={group}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-700 text-sm">{group}</h3>
                  <span className="text-sm font-bold text-slate-500">{formatRupiah(groupTotal)}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {groupSaldos.map(akun => <SaldoKartu key={akun.akunId} akun={akun} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal: Mutasi ── */}
      <Modal open={showMutasi} onClose={() => setShowMutasi(false)}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {selectedAkun && <img src={iconSrc(selectedAkun)} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }} />}
            Mutasi: {selectedAkun?.namaAkun}
          </span>
        } size="lg">
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl mb-4">
          <span className="text-sm text-slate-500">Saldo Saat Ini</span>
          <span className="text-lg font-black">{formatRupiah(selectedAkun?.saldo)}</span>
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => { setShowMutasi(false); openTopUp(selectedAkun); }} className="btn btn-success flex-1 justify-center text-xs">
            <ArrowUpCircle size={14} /> Top Up
          </button>
          {isAdmin && (
            <button onClick={() => { setShowMutasi(false); openKoreksi(selectedAkun); }} className="btn btn-outline flex-1 justify-center text-xs">
              <Edit3 size={14} /> Koreksi
            </button>
          )}
        </div>
        {mutasiLoading ? <Loader size="sm" /> : (
          <div className="max-h-96 overflow-y-auto">
            {mutasi.length === 0 ? <EmptyState message="Belum ada mutasi" />
              : mutasi.map((m, i) => {
                const jenis = getJenisMutasi(m.keterangan);
                const kasir = (typeof m.createdBy === 'object' && m.createdBy !== null)
                  ? (m.createdBy.name || 'Sistem')
                  : (m.createdBy || 'Sistem');
                return (
                  <div key={i} className="flex items-start gap-3 py-3 border-b border-slate-50 last:border-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${m.type === 'masuk' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {m.type === 'masuk' ? <TrendingUp size={14} className="text-green-600" /> : <TrendingDown size={14} className="text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${jenis.cls}`}>{jenis.label}</span>
                        <p className={`text-sm font-bold whitespace-nowrap ${m.type === 'masuk' ? 'text-green-600' : 'text-red-500'}`}>
                          {m.type === 'masuk' ? '+' : '-'}{formatRupiah(m.amount)}
                        </p>
                      </div>
                      <p className="text-sm font-medium text-slate-700 break-words">{m.keterangan}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDateTime(m.createdAt)} <span className="mx-1">|</span> oleh: {kasir} <span className="mx-1">|</span> {formatRupiah(m.saldoBefore)} → {formatRupiah(m.saldoAfter)}
                      </p>
                    </div>
                  </div>
                );
              })
            }
          </div>
        )}
      </Modal>

      {/* ── Modal: Riwayat Transaksi ── */}
      <Modal open={showRiwayatTx} onClose={() => setShowRiwayatTx(false)}
        title={`Transaksi: ${riwayatTxAkun?.icon} ${riwayatTxAkun?.namaAkun}`} size="lg">
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl mb-4">
          <span className="text-sm text-slate-500">Saldo Saat Ini</span>
          <span className="text-lg font-black">{formatRupiah(riwayatTxAkun?.saldo || 0)}</span>
        </div>
        {riwayatTxLoading ? <Loader size="sm" /> : (
          <>
            {riwayatTx.length === 0 ? <EmptyState message="Belum ada transaksi" />
              : riwayatTx.map(tx => (
                <div key={tx._id} className="mb-4 border border-slate-100 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                    <div>
                      <code className="text-xs font-mono bg-white px-2 py-0.5 rounded border border-slate-200">{tx.invoiceNumber}</code>
                      <span className="text-xs text-slate-400 ml-2">{formatDateTime(tx.transactionDate)}</span>
                    </div>
                    <span className="text-xs text-slate-500">{tx.customerName}</span>
                  </div>
                  {tx.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border-t border-slate-50">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-700">{item.productName}</p>
                        {item.targetNumber && <p className="text-xs text-slate-400">→ {item.targetNumber}</p>}
                        <div className="flex gap-3 mt-1 text-xs">
                          {item.category === 'tarik_tunai' ? (
                            <>
                              <span className="text-slate-500">Tarik: <span className="font-bold">{formatRupiah((item.modalAmount || item.purchasePrice || 0) * (item.quantity || 1))}</span></span>
                              <span className="text-slate-500">Fee: <span className="font-bold text-blue-600">{formatRupiah(item.sellPrice * (item.quantity || 1))}</span></span>
                            </>
                          ) : (
                            <>
                              <span className="text-slate-500">Modal: <span className="font-bold">{formatRupiah((item.modalAmount || item.purchasePrice || 0) * (item.quantity || 1))}</span></span>
                              <span className="text-slate-500">Jual: <span className="font-bold text-blue-600">{formatRupiah(item.sellPrice * (item.quantity || 1))}</span></span>
                            </>
                          )}
                          {item.cashback > 0 && <span className="text-green-600">CB: +{formatRupiah(item.cashback)}</span>}
                        </div>
                        {item.category === 'tarik_tunai' ? (
                          <p className="text-xs font-bold mt-1 text-green-600">
                            Fee/Jasa: {formatRupiah(item.sellPrice * (item.quantity || 1) + (item.cashback || 0))}
                          </p>
                        ) : (
                          <p className={`text-xs font-bold mt-1 ${((item.sellPrice * (item.quantity||1) - (item.modalAmount || item.purchasePrice || 0) * (item.quantity||1)) + (item.cashback || 0)) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            Laba: {formatRupiah((item.sellPrice * (item.quantity||1) - (item.modalAmount || item.purchasePrice || 0) * (item.quantity||1)) + (item.cashback || 0))}
                          </p>
                        )}
                      </div>
                      <button onClick={() => openEditTx(tx, item)} className="btn btn-outline py-1.5 px-3 text-xs flex-shrink-0">
                        <Edit3 size={12} /> Edit
                      </button>
                    </div>
                  ))}
                </div>
              ))
            }
            {riwayatTxPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400">Hal {riwayatTxPage} / {riwayatTxPages}</p>
                <div className="flex gap-2">
                  <button className="btn btn-outline py-1.5 px-3 text-xs" onClick={() => loadRiwayatTx(riwayatTxAkun, riwayatTxPage - 1)} disabled={riwayatTxPage <= 1}>←</button>
                  <button className="btn btn-outline py-1.5 px-3 text-xs" onClick={() => loadRiwayatTx(riwayatTxAkun, riwayatTxPage + 1)} disabled={riwayatTxPage >= riwayatTxPages}>→</button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── Modal: Edit Nominal Transaksi ── */}
      <Modal open={showEditTx} onClose={() => setShowEditTx(false)} title="Edit Nominal Transaksi" size="sm">
        {selectedItem && (
          <>
            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="text-sm font-bold text-slate-700">{selectedItem.productName}</p>
              {selectedItem.targetNumber && <p className="text-xs text-slate-400">→ {selectedItem.targetNumber}</p>}
              <p className="text-xs text-slate-400 mt-1">{selectedTx?.invoiceNumber}</p>
            </div>
            <div className="space-y-3">
              {[
                { label: selectedItem.category === 'tarik_tunai' ? 'Tarik (Rp)' : 'Modal (Rp)', key: 'purchasePrice' },
                { label: selectedItem.category === 'tarik_tunai' ? 'Fee/Jasa (Rp)' : 'Harga Jual (Rp)', key: 'sellPrice' },
                { label: 'Cashback (Rp)', key: 'cashback' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="label">{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">Rp</span>
                    <input className="input pl-10" inputMode="numeric" placeholder="0"
                      value={formatDisp(editForm[key])}
                      onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value.replace(/\D/g, '') }))} />
                  </div>
                </div>
              ))}
              {editForm.sellPrice && editForm.purchasePrice && (
                <div className={`flex justify-between px-3 py-2 rounded-lg text-xs ${selectedItem.category === 'tarik_tunai' ? 'bg-green-50 text-green-700' : (parseInt(editForm.sellPrice) - parseInt(editForm.purchasePrice) + (parseInt(editForm.cashback) || 0)) >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  <span>{selectedItem.category === 'tarik_tunai' ? 'Fee/Jasa estimasi' : 'Laba estimasi'}</span>
                  <span className="font-bold">{selectedItem.category === 'tarik_tunai'
                    ? formatRupiah((parseInt(editForm.sellPrice) || 0) + (parseInt(editForm.cashback) || 0))
                    : formatRupiah((parseInt(editForm.sellPrice) || 0) - (parseInt(editForm.purchasePrice) || 0) + (parseInt(editForm.cashback) || 0))}
                  </span>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button className="btn btn-outline flex-1" onClick={() => setShowEditTx(false)}>Batal</button>
              <button className="btn btn-primary flex-1" onClick={handleEditTx} disabled={editSaving}>
                {editSaving ? <Loader2 size={16} className="animate-spin" /> : <Edit3 size={16} />}
                {editSaving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Modal: Top Up ── */}
      <Modal open={showTopUp} onClose={() => setShowTopUp(false)} title="Top Up Saldo" size="sm">
        <div className="space-y-3">
          <SumberDanaSelect value={topUpForm.akunId} onChange={v => setTopUpForm(f => ({ ...f, akunId: v }))} label="Akun" />
          <div>
            <label className="label">Nominal Top Up (Rp)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">Rp</span>
              <input className="input pl-10" inputMode="numeric" placeholder="0"
                value={formatDisp(topUpForm.amount)}
                onChange={e => setTopUpForm(f => ({ ...f, amount: e.target.value.replace(/\D/g, '') }))} />
            </div>
            <div className="grid grid-cols-4 gap-1.5 mt-2">
              {[100000, 200000, 500000, 1000000].map(v => (
                <button key={v} onClick={() => setTopUpForm(f => ({ ...f, amount: String(v) }))}
                  className="py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600">
                  {v >= 1000000 ? '1jt' : `${v / 1000}rb`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Keterangan</label>
            <input className="input" placeholder="Transfer dari BCA, setoran tunai, dll..."
              value={topUpForm.keterangan} onChange={e => setTopUpForm(f => ({ ...f, keterangan: e.target.value }))} />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-outline flex-1" onClick={() => setShowTopUp(false)}>Batal</button>
          <button className="btn btn-success flex-1" onClick={handleTopUp} disabled={saving}>
            {saving ? 'Menyimpan...' : '✅ Top Up'}
          </button>
        </div>
      </Modal>

      {/* ── Modal: Transfer ── */}
      <Modal open={showTransfer} onClose={() => setShowTransfer(false)} title="Transfer Antar Akun" size="sm">
        <div className="space-y-3">
          <SumberDanaSelect value={transferForm.fromAkunId} onChange={v => setTransferForm(f => ({ ...f, fromAkunId: v }))} label="Dari Akun" />
          <SumberDanaSelect value={transferForm.toAkunId} onChange={v => setTransferForm(f => ({ ...f, toAkunId: v }))} label="Ke Akun" excludeAkunId={transferForm.fromAkunId} />
          <div>
            <label className="label">Jumlah Transfer (Rp)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">Rp</span>
              <input className="input pl-10" inputMode="numeric" placeholder="0"
                value={formatDisp(transferForm.amount)}
                onChange={e => setTransferForm(f => ({ ...f, amount: e.target.value.replace(/\D/g, '') }))} />
            </div>
          </div>
          <div>
            <label className="label">Biaya Transfer (Rp)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">Rp</span>
              <input className="input pl-10" inputMode="numeric" placeholder="0"
                value={formatDisp(transferForm.biayaTransfer)}
                onChange={e => setTransferForm(f => ({ ...f, biayaTransfer: e.target.value.replace(/\D/g, '') }))} />
            </div>
          </div>
          <div>
            <label className="label">Keterangan</label>
            <input className="input" placeholder="Isi ulang modal server pulsa, dll..."
              value={transferForm.keterangan} onChange={e => setTransferForm(f => ({ ...f, keterangan: e.target.value }))} />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-outline flex-1" onClick={() => setShowTransfer(false)}>Batal</button>
          <button className="btn btn-primary flex-1" onClick={handleTransfer} disabled={saving}>
            {saving ? 'Memproses...' : '🔄 Transfer'}
          </button>
        </div>
      </Modal>

      {/* ── Modal: Koreksi ── */}
      <Modal open={showKoreksi} onClose={() => setShowKoreksi(false)} title="Koreksi Saldo Manual" size="sm">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
          <p className="text-xs text-yellow-700">⚠️ Koreksi manual akan langsung mengubah saldo. Gunakan hanya untuk penyesuaian.</p>
        </div>
        <div className="space-y-3">
          <SumberDanaSelect value={koreksiForm.akunId} onChange={v => setKoreksiForm(f => ({ ...f, akunId: v }))} label="Akun" />
          <div>
            <label className="label">Saldo Baru (Rp)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">Rp</span>
              <input className="input pl-10" inputMode="numeric" placeholder="0"
                value={formatDisp(koreksiForm.saldoBaru)}
                onChange={e => setKoreksiForm(f => ({ ...f, saldoBaru: e.target.value.replace(/\D/g, '') }))} />
            </div>
          </div>
          <div>
            <label className="label">Alasan Koreksi</label>
            <input className="input" placeholder="Sesuai mutasi bank, selisih hitung, dll..."
              value={koreksiForm.keterangan} onChange={e => setKoreksiForm(f => ({ ...f, keterangan: e.target.value }))} />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-outline flex-1" onClick={() => setShowKoreksi(false)}>Batal</button>
          <button className="btn btn-danger flex-1" onClick={handleKoreksi} disabled={saving}>
            {saving ? 'Menyimpan...' : 'Simpan Koreksi'}
          </button>
        </div>
      </Modal>

      {/* ── Modal: Kelola Akun ── */}
      <Modal open={showKelolaAkun} onClose={() => setShowKelolaAkun(false)} title="Kelola Akun Saldo" size="lg">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">{semuaAkun.length} akun terdaftar</p>
          <button className="btn btn-primary text-xs py-2"
            onClick={() => { setAkunForm({ akunId: '', namaAkun: '', group: 'Bank', icon: '💳', iconFile: 'bank.png' }); setShowTambahAkun(true); }}>
            <Plus size={14} /> Tambah Akun
          </button>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {GROUP_ORDER.map(group => {
            const groupAkun = semuaAkun.filter(a => a.group === group);
            if (!groupAkun.length) return null;
            return (
              <div key={group}>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">{group}</p>
                {groupAkun.map(akun => (
                  <div key={akun.akunId} className={`flex items-center gap-3 p-3 rounded-xl border mb-1.5 ${!akun.isActive ? 'opacity-50 bg-slate-50' : 'bg-white border-slate-100'}`}>
                    <img src={iconSrc(akun)} alt={akun.namaAkun} className="w-8 h-8 object-contain rounded-lg" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-700">{akun.namaAkun}</p>
                      <p className="text-xs text-slate-400">{akun.akunId} • {formatRupiah(akun.saldo)}</p>
                    </div>
                    <div className="flex gap-1.5 items-center">
                      {!akun.isActive && <span className="badge badge-red text-xs">Nonaktif</span>}
                      <button onClick={() => {
                        setSelectedAkunEdit(akun);
                        const savedAllowed = Array.isArray(akun.allowedMenus) ? akun.allowedMenus : [];
                        const savedOrder = Array.isArray(akun.menuOrder) ? akun.menuOrder : [];
                        const baseOrder = savedOrder.length > 0 ? savedOrder : DEFAULT_MENU_ORDER;
                        const backfilled = [...baseOrder, ...DEFAULT_MENU_ORDER.filter(k => !baseOrder.includes(k))];
                        setAkunForm({
                          namaAkun: akun.namaAkun,
                          group: akun.group,
                          icon: akun.icon,
                          iconFile: akun.iconFile || getAkunIcon(akun.akunId),
                          isActive: akun.isActive !== false,
                          allowedMenus: savedAllowed.length > 0 ? [...savedAllowed] : DEFAULT_MENU_ORDER.slice(),
                          menuOrder: backfilled,
                        });
                        setShowEditAkun(true);
                      }} className="btn btn-outline py-1 px-2 text-xs">
                        <Edit3 size={12} /> Edit
                      </button>
                      {akun.akunId !== 'tunai' && akun.isActive && (
                        <button onClick={() => handleDeleteAkun(akun)} className="btn btn-danger py-1 px-2 text-xs">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Modal>

      {/* ── Modal: Tambah Akun ── */}
      <Modal open={showTambahAkun} onClose={() => setShowTambahAkun(false)} title="Tambah Akun Saldo Baru" size="sm">
        <div className="space-y-3">
          <div>
            <label className="label">ID Akun * <span className="text-slate-400 font-normal text-xs">(unik, tanpa spasi)</span></label>
            <input className="input" placeholder="contoh: bni, dana3, shopee_pay"
              value={akunForm.akunId}
              onChange={e => setAkunForm(f => ({ ...f, akunId: e.target.value.toLowerCase().replace(/\s/g, '_') }))} />
            <p className="text-xs text-slate-400 mt-1">Huruf kecil dan underscore. Contoh: bni, dana3</p>
          </div>
          <div>
            <label className="label">Nama Akun *</label>
            <input className="input" placeholder="BNI, DANA 3, ShopeePay, dll"
              value={akunForm.namaAkun}
              onChange={e => setAkunForm(f => ({ ...f, namaAkun: e.target.value }))} />
          </div>
          <div>
            <label className="label">Grup *</label>
            <select className="input" value={akunForm.group} onChange={e => setAkunForm(f => ({ ...f, group: e.target.value }))}>
              <option value="Server Pulsa">Server Pulsa</option>
              <option value="Bank">Bank</option>
              <option value="E-Wallet">E-Wallet</option>
              <option value="Tunai">Tunai</option>
            </select>
          </div>
          <div>
            <label className="label">Icon</label>
            <div className="grid grid-cols-6 gap-2 p-3 bg-slate-50 rounded-xl">
              {ICON_OPTIONS.map(opt => {
                const selected = akunForm.iconFile === opt.file;
                return (
                  <button
                    key={opt.file}
                    type="button"
                    onClick={() => setAkunForm(f => ({ ...f, iconFile: opt.file }))}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition ${selected ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-white hover:bg-slate-100'}`}
                  >
                    <img src={`/icons/${opt.file}`} alt={opt.label} className="w-12 h-12 object-contain rounded-lg" />
                    <span className={`text-[10px] font-medium leading-tight text-center ${selected ? 'text-blue-700' : 'text-slate-500'}`}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-outline flex-1" onClick={() => setShowTambahAkun(false)}>Batal</button>
          <button className="btn btn-primary flex-1" onClick={handleTambahAkun} disabled={savingAkun}>
            {savingAkun ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {savingAkun ? 'Menyimpan...' : 'Tambah Akun'}
          </button>
        </div>
      </Modal>

      {/* ── Modal: Edit Akun ── */}
      <Modal open={showEditAkun} onClose={() => setShowEditAkun(false)} title={`Edit: ${selectedAkunEdit?.namaAkun}`} size="sm">
        <div className="space-y-3">
          <div>
            <label className="label">ID Akun</label>
            <input className="input bg-slate-50" value={selectedAkunEdit?.akunId} disabled />
            <p className="text-xs text-slate-400 mt-1">ID tidak bisa diubah</p>
          </div>
          <div>
            <label className="label">Nama Akun *</label>
            <input className="input" placeholder="Nama akun"
              value={akunForm.namaAkun} onChange={e => setAkunForm(f => ({ ...f, namaAkun: e.target.value }))} />
          </div>
          <div>
            <label className="label">Grup *</label>
            <select className="input" value={akunForm.group} onChange={e => setAkunForm(f => ({ ...f, group: e.target.value }))}>
              <option value="Server Pulsa">Server Pulsa</option>
              <option value="Bank">Bank</option>
              <option value="E-Wallet">E-Wallet</option>
              <option value="Tunai">Tunai</option>
            </select>
          </div>
          <div>
            <label className="label">Icon</label>
            <div className="grid grid-cols-6 gap-2 p-3 bg-slate-50 rounded-xl">
              {ICON_OPTIONS.map(opt => {
                const selected = akunForm.iconFile === opt.file;
                return (
                  <button
                    key={opt.file}
                    type="button"
                    onClick={() => setAkunForm(f => ({ ...f, iconFile: opt.file }))}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition ${selected ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-white hover:bg-slate-100'}`}
                  >
                    <img src={`/icons/${opt.file}`} alt={opt.label} className="w-12 h-12 object-contain rounded-lg" />
                    <span className={`text-[10px] font-medium leading-tight text-center ${selected ? 'text-blue-700' : 'text-slate-500'}`}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {akunForm.group !== 'Tunai' && (
            <div className="p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between mb-2 gap-2">
                <label className="text-sm font-bold text-slate-700">Menu Transaksi Digital</label>
                <div className="flex gap-1.5 items-center flex-shrink-0">
                  <button type="button"
                    onClick={() => setAkunForm(f => ({ ...f, allowedMenus: DIGITAL_MENU_OPTIONS.map(o => o.key) }))}
                    className="text-xs text-blue-600 hover:text-blue-700 font-semibold">
                    Pilih Semua
                  </button>
                  <span className="text-slate-300 text-xs">·</span>
                  <button type="button"
                    onClick={() => setAkunForm(f => ({ ...f, allowedMenus: [] }))}
                    className="text-xs text-red-500 hover:text-red-600 font-semibold">
                    Hapus Semua
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {(() => {
                  const currentOrder = (akunForm.menuOrder?.length > 0 ? akunForm.menuOrder : DEFAULT_MENU_ORDER);
                  const backfilled = [...currentOrder, ...DEFAULT_MENU_ORDER.filter(k => !currentOrder.includes(k))];
                  return backfilled
                    .map(k => DIGITAL_MENU_OPTIONS.find(o => o.key === k))
                    .filter(Boolean)
                    .map(opt => {
                      const checked = (akunForm.allowedMenus || []).includes(opt.key);
                      const isDragging = draggedMenuKey === opt.key;
                      return (
                        <div key={opt.key}
                          draggable={checked}
                          onDragStart={(e) => {
                            if (!checked) return;
                            setDraggedMenuKey(opt.key);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            if (!draggedMenuKey) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (!draggedMenuKey || draggedMenuKey === opt.key) return;
                            setAkunForm(f => {
                              const cur = (f.menuOrder?.length > 0 ? f.menuOrder : DEFAULT_MENU_ORDER);
                              const arr = [...cur, ...DEFAULT_MENU_ORDER.filter(k => !cur.includes(k))];
                              const from = arr.indexOf(draggedMenuKey);
                              const to   = arr.indexOf(opt.key);
                              if (from === -1 || to === -1) return f;
                              arr.splice(from, 1);
                              arr.splice(to, 0, draggedMenuKey);
                              return { ...f, menuOrder: arr };
                            });
                            setDraggedMenuKey(null);
                          }}
                          onDragEnd={() => setDraggedMenuKey(null)}
                          className={`flex items-center gap-2 p-2 rounded-lg border transition ${isDragging ? 'opacity-40' : ''} ${checked ? 'bg-blue-50 border-blue-200 cursor-move' : 'bg-white border-slate-100'}`}>
                          <GripVertical size={14} className={`flex-shrink-0 ${checked ? 'text-slate-400' : 'text-slate-200'}`} />
                          <input type="checkbox" className="rounded"
                            checked={checked}
                            onChange={() => setAkunForm(f => {
                              const list = f.allowedMenus || [];
                              return {
                                ...f,
                                allowedMenus: checked ? list.filter(k => k !== opt.key) : [...list, opt.key],
                              };
                            })} />
                          <span className="text-xs font-medium text-slate-700 flex-1">{opt.label}</span>
                        </div>
                      );
                    });
                })()}
              </div>
              <p className="text-xs text-slate-400 mt-2">Drag ⠿ menu tercentang untuk atur urutan tab di Transaksi. Kosongkan semua = default (semua menu aktif).</p>
            </div>
          )}
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <input type="checkbox" id="isActiveEdit" checked={akunForm.isActive !== false}
              onChange={e => setAkunForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
            <label htmlFor="isActiveEdit" className="text-sm font-medium text-slate-700">Akun Aktif</label>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button className="btn btn-outline flex-1" onClick={() => setShowEditAkun(false)}>Batal</button>
          <button className="btn btn-primary flex-1" onClick={handleUpdateAkun} disabled={savingAkun}>
            {savingAkun ? <Loader2 size={16} className="animate-spin" /> : <Edit3 size={16} />}
            {savingAkun ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </Modal>
    </div>
  );
}